// Stencil
import { Component, Element, h, Host, Prop, State } from "@stencil/core";

// Other Libraries
import { getIconPath, MercuryBundles } from "@genexus/mercury";

// Custom Imports
import {
  ChEditCustomEvent,
  TabularGridColumnSortChangedEvent,
  TabularGridRowClickedEvent
} from "@genexus/chameleon-controls-library";
import { RadioGroupItemModel } from "@genexus/chameleon-controls-library/dist/types/components/radio-group/types";
import { getAssetPath, JSX, Watch } from "@stencil/core/internal";
import { config } from "../../../common/config";
import {
  clearControlError,
  ControlValidation,
  validateControls
} from "../../../common/form-validation";
import { Locale } from "../../../common/locale";
import { FormSubmitResult } from "../../../common/types";

import sortTabularGridColumn from "../../../common/chameleon/tabular-grid-column-sort";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "components/tooltip",
  "chameleon/scrollbar",
  "components/tabular-grid"
];

const INFO_ICON = getIconPath({
  category: "system",
  name: "information",
  colorType: "on-surface"
});

const LOCK_ICON = getIconPath({
  category: "system",
  name: "lock",
  colorType: "on-surface"
});

const LANGUAGE_ICON = getIconPath({
  category: "system",
  name: "language",
  colorType: "on-surface"
});

const DELETE_ICON = getIconPath({
  category: "system",
  name: "delete-outlined",
  colorType: "interactive"
});

const ERROR_ICON = getIconPath({
  category: "system",
  name: "error",
  colorType: "error"
});

const SEARCH_ICON = getIconPath({
  category: "system",
  name: "search",
  colorType: "on-surface"
});

const SERVER_NAME_PREFIX = "server-name-";

@Component({
  tag: "gx-ide-server-selector",
  styleUrl: "server-selector.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/server-selector"]
})
export class GxIdeServerSelector {
  #_componentLocale: any;

  #chGridAllServersEl: HTMLChTabularGridElement;
  #serverUrlEL: HTMLChEditElement;
  #serverFriendlyNameEL: HTMLChEditElement;
  #updatedServerName: string | undefined;
  #serverNameEditElsMap: Map<string, HTMLChEditElement> = new Map();
  #versionsSelect: RadioGroupItemModel[] = [];
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/server-selector/shortcuts.json`
  );
  @Element() el: HTMLGxIdeServerSelectorElement;

  /**
   * State to enable/disable confirm button.
   */
  @State() enableConfirmButton: boolean = false;

  /**
   * The current server URL input value.
   */
  @State() serverUrlInputValue: string = "";

  /**
   * The current server URL input value.
   */
  @State() serverNameInputValue: string = "";

  /**
   * The selected server URI.
   */
  @State() selectedServerUri: string | undefined;

  /**
   * The selected server name.
   */
  @State() selectedServerName: string | undefined = "None";

  /**
   * The user name of the selected server.
   */
  @State() userName: string;

  /**
   * State to store the filter input value to filter the list-box
   */
  @State() filterValue: string = "";

  /**
   * Servers filtered
   */
  @State() filteredServersData: ServerData[] = [];

  @State() validatableControls = new Map<string, ControlValidation>();

  /**
   * The selected connection type
   */
  @State() selectedConnectionType: ConnectionOption = "select";

  /**
   * The selected recently used server ID
   */
  @State() selectedRecentlyUsedId: string;

  /**
   * The list of all GX servers.
   */
  @Prop({ mutable: true }) serversData: ServerData[] = [];
  @Watch("serversData")
  serversDataChanged(newValue: ServerData[]) {
    this.filteredServersData = newValue;
  }
  /**
   * The list of recently used servers.
   */
  @Prop() readonly recentlyUsedServersData: ServerData[] = [];

  /**
   * Callback that is invoked when the dialog is closed
   */
  @Prop() readonly cancelCallback!: () => Promise<void>;

  /**
   * Callback that must be invoked when the 'Confirm' button is pressed and the Select Server option is checked.
   */
  @Prop() readonly selectServerCallback!: (
    authenticationSessionOrigin: AuthenticationSessionOrigin,
    connectionOption: ConnectionOption
  ) => Promise<FormSubmitResult>;

  /**
   * Invoked to delete a server
   */
  @Prop() readonly deleteServerCallback!: (
    serverData: ServerData
  ) => Promise<boolean>;

  /**
   * Invoked to update a server
   */
  @Prop() readonly updateServerCallback!: (
    serverData: ServerData
  ) => Promise<FormSubmitResult>;

  async componentWillLoad() {
    this.#_componentLocale = await Locale.getComponentStrings(this.el);

    this.#versionsSelect = [
      {
        value: "select",
        caption: this.#_componentLocale.captions.selectServer
      },
      {
        value: "new",
        caption: this.#_componentLocale.captions.addServer
      }
    ];

    this.selectedServerUri = this.serversData[0]?.serverUrl;
    this.userName = this.serversData[0]?.gxServerUsername;

    this.filteredServersData = this.serversData;
  }

  componentDidRender() {
    if (
      this.selectedConnectionType === "new" &&
      this.#serverUrlEL &&
      this.#serverFriendlyNameEL
    ) {
      this.#initializeValidatableControls([
        this.#serverUrlEL,
        this.#serverFriendlyNameEL
      ]);
      this.#serverNameEditElsMap.clear();
    }

    if (
      this.selectedConnectionType === "select" &&
      this.#serverNameEditElsMap.size > 0
    ) {
      this.#initializeValidatableControls([
        ...this.#serverNameEditElsMap.values()
      ]);
    }
  }

  #cancelHandler = (): void => {
    this.cancelCallback();
  };

  #confirmHandler = async (): Promise<void> => {
    let formSubmitResult: FormSubmitResult;
    if (this.selectedConnectionType === "select") {
      formSubmitResult = await this.selectServerCallback(
        {
          uri: this.selectedServerUri,
          name: this.selectedServerName
        },
        this.selectedConnectionType
      );
    } else {
      formSubmitResult = await this.selectServerCallback(
        {
          uri: this.serverUrlInputValue,
          name: this.serverNameInputValue
        },
        this.selectedConnectionType
      );
      this.validatableControls = validateControls(
        formSubmitResult,
        this.validatableControls
      );
    }
  };

  // TODO: evaluate if this should be a global helper function.
  // It is used on other dialogs as well.
  #evaluateTooltipRender = (
    controlReference: HTMLElement
  ): HTMLChTooltipElement =>
    this.validatableControls?.get(controlReference?.id)?.hasError &&
    this.validatableControls.get(controlReference.id)?.message && (
      <ch-tooltip
        class="tooltip"
        actionElement={controlReference as unknown as HTMLButtonElement}
        blockAlign={config.tooltipSettings.blockAlign}
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delay}
      >
        {this.validatableControls.get(controlReference.id).message}
      </ch-tooltip>
    );

  #initializeValidatableControls = (validatableControls: HTMLElement[]) => {
    validatableControls.forEach(validatableControl => {
      if (
        validatableControl.id &&
        !this.validatableControls.has(validatableControl.id)
      ) {
        this.validatableControls.set(validatableControl.id, {
          reference: validatableControl,
          hasError: false,
          message: undefined
        });
      }
    });
  };

  #filterValueChangedHandler = (
    event: ChEditCustomEvent<string> | InputEvent
  ) => {
    this.filterValue = (event.detail as string).toLowerCase();
    this.filteredServersData = this.serversData.filter(item =>
      item.serverName.toLowerCase().includes(this.filterValue)
    );
  };

  #inputHandler = (
    event: ChEditCustomEvent<string> | InputEvent,
    field: "serverUrlInputValue" | "serverNameInputValue"
  ) => {
    this.enableConfirmButton = false;
    this[field] = (event.detail as string).trim();

    const controlId =
      field === "serverUrlInputValue" ? "server-url" : "server-name";
    clearControlError(this.validatableControls, controlId);
    this.#enableConfirmButton();
  };

  #handleRecentlyUsedCardClick = async (server: ServerData) => {
    this.selectedRecentlyUsedId = server.id;
    await this.#chGridAllServersEl?.selectRow(server.id);
    await this.#handleServerSelection(server.id);
  };

  #handleDeleteServer = async (serverData: ServerData) => {
    if (serverData.accessLevel === "private") {
      const result = await this.deleteServerCallback(serverData);
      if (result) {
        this.selectedServerName = "None";
        this.enableConfirmButton = false;
      }
    }
  };

  #onServerNameInput = async (
    event: ChEditCustomEvent<string> | InputEvent
  ) => {
    this.#updatedServerName = event.detail as string;
  };

  #onServerNameChange = async (serverData: ServerData) => {
    let result: FormSubmitResult = {
      fieldErrors: [],
      success: false
    };
    if (
      serverData.serverName !== this.#updatedServerName &&
      serverData.accessLevel === "private"
    ) {
      result = await this.updateServerCallback({
        ...serverData,
        serverName: this.#updatedServerName
      });
    }

    this.validatableControls = validateControls(
      result,
      this.validatableControls
    );

    if (result.success || serverData.serverName === this.#updatedServerName) {
      this.selectedServerName = this.#updatedServerName;
      clearControlError(
        this.validatableControls,
        `${SERVER_NAME_PREFIX}${serverData.id}`
      );
    }
  };

  #enableConfirmButton = () => {
    if (
      this.serverUrlInputValue &&
      this.serverUrlInputValue.length > 0 &&
      this.serverNameInputValue &&
      this.serverNameInputValue.length > 0
    ) {
      this.enableConfirmButton = true;
    } else {
      this.enableConfirmButton = false;
    }
  };

  #onRecentlyUsedDoubleClickHandler = (serverData: ServerData) => {
    this.#handleServerSelection(serverData.id);
    this.#confirmHandler();
  };

  #onServersRowClickHandler = (
    event: CustomEvent<TabularGridRowClickedEvent>
  ) => this.#handleServerSelection(event.detail.rowId);

  #handleServerSelection = async (selectedRowId: string): Promise<void> => {
    if (selectedRowId === undefined) {
      this.enableConfirmButton = false;
      this.selectedServerUri = undefined;
      this.selectedServerName = undefined;
      return;
    }

    const selectedServer = this.serversData.find(
      server => server.id === selectedRowId
    );
    this.enableConfirmButton = true;
    this.selectedServerName = selectedServer?.serverName;
    this.selectedServerUri = selectedServer?.serverUrl;
    this.userName = selectedServer?.gxServerUsername || "";
    this.selectedRecentlyUsedId = selectedServer?.id;
  };

  #connectionTypeChangedHandler = (event: CustomEvent<string>) => {
    this.selectedConnectionType = event.detail as ConnectionOption;
    if (this.selectedConnectionType === "new") {
      this.enableConfirmButton = false;
      this.serverUrlInputValue = "";
    }
    this.selectedServerName = "None";
  };

  #sortTabularGridColumnCallback = (
    e: CustomEvent<TabularGridColumnSortChangedEvent>
  ) => {
    this.filteredServersData = [
      ...sortTabularGridColumn(e, this.filteredServersData, "string", undefined)
    ];
  };

  #renderTabularGridColumnSet = (
    hideColumnName: boolean = false
  ): HTMLChTabularGridColumnsetElement => {
    return (
      <ch-tabular-grid-columnset class="tabular-grid-column-set">
        <ch-tabular-grid-column
          columnId="icon"
          class="tabular-grid-column tabular-grid-column-icon"
          columnName="Icon"
          settingable={false}
          columnNameHidden={true}
          size={config.tabularGrid.colSize.maxContent}
          resizable={false}
          sortable={false}
        ></ch-tabular-grid-column>
        <ch-tabular-grid-column
          columnId="serverName"
          class="tabular-grid-column"
          columnName="Server Name"
          settingable={false}
          columnNameHidden={hideColumnName}
          size={config.tabularGrid.colSize.serverName}
          resizable={false}
          sortable={!hideColumnName}
          onColumnSortChanged={this.#sortTabularGridColumnCallback}
        ></ch-tabular-grid-column>
        <ch-tabular-grid-column
          columnId="serverUrl"
          class="tabular-grid-column"
          columnName="Server Url"
          settingable={false}
          columnNameHidden={hideColumnName}
          size={config.tabularGrid.colSize.auto}
          resizable={false}
          sortable={!hideColumnName}
          onColumnSortChanged={this.#sortTabularGridColumnCallback}
        ></ch-tabular-grid-column>
        <ch-tabular-grid-column
          columnId="userName"
          class="tabular-grid-column"
          columnName="Connected"
          settingable={false}
          columnNameHidden={hideColumnName}
          size={config.tabularGrid.colSize.userName}
          resizable={false}
          sortable={!hideColumnName}
          onColumnSortChanged={this.#sortTabularGridColumnCallback}
        ></ch-tabular-grid-column>
        <ch-tabular-grid-column
          columnId="action"
          class="tabular-grid-column tabular-grid-column-icon"
          columnName="Action"
          settingable={false}
          columnNameHidden={true}
          size={config.tabularGrid.colSize.maxContent}
          resizable={false}
          sortable={false}
        ></ch-tabular-grid-column>
      </ch-tabular-grid-columnset>
    );
  };

  #renderTooltip = (text: string): JSX.Element | null => {
    return text !== "" ? (
      <ch-tooltip
        class="tooltip"
        actionElement={null}
        blockAlign="outside-end"
        inlineAlign={config.tooltipSettings.inlineAlign}
        delay={config.tooltipSettings.delayLong}
      >
        {text}
      </ch-tooltip>
    ) : null;
  };

  #renderServerNameErrorMessage = (errorMsg: string) => {
    return (
      <ch-tabular-grid-rowset class="tabular-grid-rowset">
        <ch-tabular-grid-rowset-empty>
          <p class="warning-error-description">
            <ch-image
              class="icon-m warning-error-icon"
              src={ERROR_ICON}
            ></ch-image>
            {errorMsg}
          </p>
        </ch-tabular-grid-rowset-empty>
      </ch-tabular-grid-rowset>
    );
  };

  #renderTabularGridCommonCells = (serverData: ServerData) => {
    return [
      <ch-tabular-grid-cell class="tabular-grid-cell">
        {this.#renderIcon(serverData.accessLevel)}
      </ch-tabular-grid-cell>,
      <ch-tabular-grid-cell class="tabular-grid-cell">
        {serverData.accessLevel === "private" ? (
          <ch-edit
            id={`${SERVER_NAME_PREFIX}${serverData.id}`}
            value={serverData.serverName}
            onInput={this.#onServerNameInput}
            onChange={() => this.#onServerNameChange(serverData)}
            maxLength={18}
            ref={el => {
              const key = `${SERVER_NAME_PREFIX}${serverData.id}`;
              if (el) {
                this.#serverNameEditElsMap.set(key, el as HTMLChEditElement);
              } else {
                this.#serverNameEditElsMap.delete(key);
              }
            }}
          ></ch-edit>
        ) : (
          serverData.serverName
        )}
      </ch-tabular-grid-cell>,
      <ch-tabular-grid-cell class="tabular-grid-cell">
        {serverData.serverUrl}
        {this.#renderTooltip(serverData.serverUrl)}
      </ch-tabular-grid-cell>,
      <ch-tabular-grid-cell class="tabular-grid-cell">
        {serverData.gxServerUsername}
      </ch-tabular-grid-cell>,
      <ch-tabular-grid-cell
        class="tabular-grid-cell"
        onClick={() => this.#handleDeleteServer(serverData)}
      >
        <button
          id="delete-button"
          class="button-tertiary button-icon-only"
          aria-label={this.#_componentLocale.actions.deleteServer}
          disabled={serverData.accessLevel === "public"}
        >
          <ch-image
            disabled={serverData.accessLevel === "public"}
            class="icon-m delete-server-icon"
            src={DELETE_ICON}
          ></ch-image>
        </button>
      </ch-tabular-grid-cell>
    ];
  };

  #renderAllServersGrid = (): Element => {
    return (
      <div class="tabular-grid-wrapper">
        <div class="border-patch"></div>
        <ch-tabular-grid
          class={"tabular-grid tabular-grid-all-servers"}
          rowSelectionMode="single"
          ref={(el: HTMLChTabularGridElement) =>
            (this.#chGridAllServersEl = el)
          }
          allowColumnReorder={false}
          onRowClicked={this.#onServersRowClickHandler}
          id="all-servers"
        >
          {this.#renderTabularGridColumnSet()}
          {this.filteredServersData.length > 0 ? (
            <ch-tabular-grid-rowset class="tabular-grid-rowset">
              {this.filteredServersData.map((serverData: ServerData) => {
                const serverNameEditKey = `${SERVER_NAME_PREFIX}${serverData.id}`;
                const hasError =
                  this.validatableControls.get(serverNameEditKey)?.hasError ===
                  true;
                const errorMsg =
                  this.validatableControls.get(serverNameEditKey)?.message;

                return (
                  <ch-tabular-grid-row
                    rowid={serverData.id}
                    class="tabular-grid-row"
                  >
                    {this.#renderTabularGridCommonCells(serverData)}
                    {hasError && this.#renderServerNameErrorMessage(errorMsg)}
                  </ch-tabular-grid-row>
                );
              })}
            </ch-tabular-grid-rowset>
          ) : (
            <ch-tabular-grid-rowset class="tabular-grid-rowset">
              <ch-tabular-grid-rowset-empty>
                <gx-ide-empty-state
                  class="empty-state"
                  stateIconSrc={INFO_ICON}
                  stateTitle={this.#_componentLocale.emptyConnectedServers}
                  isAnimated={true}
                ></gx-ide-empty-state>
              </ch-tabular-grid-rowset-empty>
            </ch-tabular-grid-rowset>
          )}
        </ch-tabular-grid>
      </div>
    );
  };

  #renderIcon = (accessLevel: string): JSX.Element => {
    const icon = accessLevel === "private" ? LOCK_ICON : LANGUAGE_ICON;
    return <ch-image class="icon-m" src={icon}></ch-image>;
  };

  #renderFooter = () => {
    const isSelectionMode = this.selectedConnectionType === "select";
    return (
      <footer
        class={{
          "dialog-footer-with-border": true,
          "dialog-footer-space-between":
            this.selectedConnectionType === "select",
          "space-between": isSelectionMode,
          "spacing-body-inline": true,
          "spacing-body-block": true
        }}
      >
        {isSelectionMode && (
          <p class="body-italic-m">Selected: {this.selectedServerName}</p>
        )}
        <div class="buttons-spacer">
          <button
            // cancel button
            id="cancel-button"
            class="button-secondary"
            onClick={this.#cancelHandler}
          >
            {this.#_componentLocale.footer.cancel}
          </button>

          <button
            // confirm button
            id="confirm-button"
            class="button-primary"
            onClick={this.enableConfirmButton && this.#confirmHandler}
            disabled={!this.enableConfirmButton}
          >
            {this.#_componentLocale.footer.confirm}
          </button>
        </div>
      </footer>
    );
  };

  #renderRecentlyUsed = () => {
    const renderRecentlyUsed =
      this.selectedConnectionType === "select" &&
      this.recentlyUsedServersData.length > 0;

    return (
      renderRecentlyUsed && (
        <div class="field field-block spacing-body-inline">
          <label class="label" htmlFor="recently-used-servers">
            {this.#_componentLocale.recentlyUsed.title}
          </label>
          <div
            class={{
              "layout": true,
              "layout--cols-3": this.recentlyUsedServersData.length >= 3,
              "layout--cols-2": this.recentlyUsedServersData.length <= 2
            }}
          >
            {this.recentlyUsedServersData.map(
              (server: ServerData, index: number) => (
                <button
                  id={"recently-used-server-" + index}
                  class={{
                    "layout__panel": true,
                    "recently-used-card": true,
                    "recently-used-card--selected":
                      this.selectedRecentlyUsedId === server.id
                  }}
                  onClick={() => this.#handleRecentlyUsedCardClick(server)}
                  onDblClick={() =>
                    this.#onRecentlyUsedDoubleClickHandler(server)
                  }
                >
                  <div class="recently-used-card-content">
                    <p class="body-regular-s text-neutral-gray-400">
                      {server.serverName}
                    </p>
                    <p class="body-regular-s text-neutral-gray-400 text-decoration-line-underline">
                      {server.serverUrl}
                    </p>
                  </div>
                  {this.#renderTooltip(server.serverUrl)}
                </button>
              )
            )}
          </div>
        </div>
      )
    );
  };

  #renderAllServers = () => {
    return (
      <div class="field field-block field-block-all-servers spacing-body-inline">
        <label class="label" htmlFor="all-servers">
          {this.#_componentLocale.allServers.title}
        </label>
        {this.#renderAllServersGrid()}
      </div>
    );
  };

  render(): void {
    return (
      <Host
        class={{
          "widget elevation-1": true,
          "new-connection": this.selectedConnectionType === "new",
          "recently-used-empty": this.recentlyUsedServersData.length === 0
        }}
      >
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts src={this.#shortcutsSrc}></ch-shortcuts>
        <main class="section">
          <header class="header spacing-body-block-start spacing-body-inline">
            <div class="field-group">
              <label class="body-regular-m text-neutral-gray-400">
                {this.#_componentLocale.header.title}
              </label>
              <ch-radio-group-render
                class="radio-group"
                model={this.#versionsSelect}
                onChange={this.#connectionTypeChangedHandler}
                value={this.#versionsSelect[0].value}
              ></ch-radio-group-render>

              {this.selectedConnectionType === "select" && (
                <ch-edit
                  class="input search-server"
                  id="server-name-search"
                  startImgSrc={SEARCH_ICON}
                  onInput={this.#filterValueChangedHandler}
                  placeholder={
                    this.#_componentLocale.placeholders.searchServers
                  }
                  accessibleName="search server"
                  type="search"
                ></ch-edit>
              )}
            </div>
          </header>

          {this.selectedConnectionType === "select" && [
            this.#renderRecentlyUsed(),
            this.#renderAllServers()
          ]}

          {this.selectedConnectionType === "new" && (
            <section class="spacing-body-inline">
              <div class="layout layout--cols-2">
                <div class="field field-block layout__panel">
                  <label class="label" htmlFor="server-url">
                    {this.#_componentLocale.labels.serverUrl}
                  </label>
                  <ch-edit
                    // server url
                    class="input"
                    id="server-url"
                    value={this.serverUrlInputValue}
                    onInput={event =>
                      this.#inputHandler(event, "serverUrlInputValue")
                    }
                    placeholder={this.#_componentLocale.placeholders.serverUrl}
                    ref={el => (this.#serverUrlEL = el as HTMLChEditElement)}
                  ></ch-edit>
                  {this.#evaluateTooltipRender(this.#serverUrlEL)}
                </div>

                <div class="field field-block layout__panel">
                  <label class="label" htmlFor="server-name">
                    {this.#_componentLocale.labels.serverName}
                  </label>
                  <ch-edit
                    // server friendly name
                    class="input"
                    id="server-name"
                    value={this.serverNameInputValue}
                    onInput={event =>
                      this.#inputHandler(event, "serverNameInputValue")
                    }
                    placeholder={this.#_componentLocale.placeholders.serverName}
                    maxLength={18}
                    ref={el =>
                      (this.#serverFriendlyNameEL = el as HTMLChEditElement)
                    }
                  ></ch-edit>
                  {this.#evaluateTooltipRender(this.#serverFriendlyNameEL)}
                </div>
              </div>
            </section>
          )}

          {this.#renderFooter()}
        </main>
      </Host>
    );
  }
}

export type ConnectionResultData = {
  formResult: FormSubmitResult;
};

export type ConnectionOption = "select" | "new";

export type ServerAccessLevel = "public" | "private";

export type AuthenticationSessionOrigin = {
  uri: string;
  name: string;
};

export type ServerData = {
  id: string;
  serverUrl: string;
  accessLevel: ServerAccessLevel;
  masterSessionUsername: string;
  serverName?: string;
  sessionId?: string;
  gxServerUsername: string;
  isConnected: boolean;
};

export type NewServerConnectionData = {
  serverUrl: string;
  serverName: string;
};
